/**
 * useCalendarSocket Hook
 * WebSocket integration for real-time calendar event updates
 *
 * Listens to calendar events from `/calendar` namespace:
 * - calendar.event.created
 * - calendar.event.updated (drag & drop, edits)
 * - calendar.event.deleted
 */

import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWebSocket } from './useWebSocket';
import { calendarsKeys } from './useCalendars';
import type {
  CalendarEventCreatedEvent,
  CalendarEventUpdatedEvent,
  CalendarEventDeletedEvent,
} from '@/api/types/calendars.types';

interface UseCalendarSocketOptions {
  /** Enable WebSocket connection */
  enabled?: boolean;
  /** Filter by calendar IDs (empty = all calendars) */
  calendarIds?: string[];
  /** Filter by resource IDs (optional, for event visibility) */
  selectedResourceIds?: string[];
  /** Callback when event is created */
  onEventCreated?: (data: CalendarEventCreatedEvent['data']) => void;
  /** Callback when event is updated */
  onEventUpdated?: (data: CalendarEventUpdatedEvent['data']) => void;
  /** Callback when event is deleted */
  onEventDeleted?: (data: CalendarEventDeletedEvent['data']) => void;
}

/**
 * Hook for subscribing to calendar WebSocket events
 *
 * @example
 * ```tsx
 * useCalendarSocket({
 *   enabled: true,
 *   calendarIds: ['cal-123'],
 *   selectedResourceIds: ['res-1', 'res-2'],
 *   onEventUpdated: (data) => {
 *     message.info(`Event updated: ${data.event.title}`);
 *   }
 * });
 * ```
 */
export function useCalendarSocket(options: UseCalendarSocketOptions = {}) {
  const {
    enabled = true,
    calendarIds = [],
    selectedResourceIds = [],
    onEventCreated,
    onEventUpdated,
    onEventDeleted,
  } = options;

  const queryClient = useQueryClient();

  // Connect to /calendar namespace
  const { socket, connected } = useWebSocket({
    namespace: '/calendar',
    enabled,
    autoConnect: true,
  });

  /**
   * Check if event should be displayed based on filters
   */
  const shouldDisplayEvent = useCallback(
    (eventCalendarId: string, eventResourceIds: string[]) => {
      // Filter by calendar ID
      if (calendarIds.length > 0 && !calendarIds.includes(eventCalendarId)) {
        return false;
      }

      // Filter by resource IDs (if filter is active)
      if (selectedResourceIds.length > 0) {
        // Event must have at least one resource that matches filter
        const hasMatchingResource = eventResourceIds.some((resId) =>
          selectedResourceIds.includes(resId)
        );
        if (!hasMatchingResource) {
          return false;
        }
      }

      return true;
    },
    [calendarIds, selectedResourceIds]
  );

  /**
   * Invalidate React Query cache for calendar events
   */
  const invalidateCalendarQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: calendarsKeys.events.all });
  }, [queryClient]);

  // Set up event listeners
  useEffect(() => {
    if (!socket || !connected) return;

    console.log('📅 [useCalendarSocket] Setting up calendar event listeners');

    /**
     * Handle calendar.event.created
     */
    const handleEventCreated = (event: CalendarEventCreatedEvent) => {
      console.log('📅 [WebSocket] calendar.event.created:', event.data);

      // Check if event should be displayed
      if (shouldDisplayEvent(event.data.calendar_id, event.data.resource_ids)) {
        console.log('📅 [WebSocket] Event matches filters, invalidating cache');
        invalidateCalendarQueries();
        onEventCreated?.(event.data);
      } else {
        console.log('📅 [WebSocket] Event filtered out (not in view)');
      }
    };

    /**
     * Handle calendar.event.updated (DRAG & DROP!)
     */
    const handleEventUpdated = (event: CalendarEventUpdatedEvent) => {
      console.log('📅 [WebSocket] calendar.event.updated:', event.data);

      // Check if event should be displayed
      if (shouldDisplayEvent(event.data.calendar_id, event.data.resource_ids)) {
        console.log('📅 [WebSocket] Event matches filters, invalidating cache');
        invalidateCalendarQueries();
        onEventUpdated?.(event.data);
      } else {
        console.log('📅 [WebSocket] Event filtered out (not in view)');
      }
    };

    /**
     * Handle calendar.event.deleted
     */
    const handleEventDeleted = (event: CalendarEventDeletedEvent) => {
      console.log('📅 [WebSocket] calendar.event.deleted:', event.data);

      // Always invalidate on delete (event might disappear from view)
      if (
        calendarIds.length === 0 ||
        calendarIds.includes(event.data.calendar_id)
      ) {
        console.log('📅 [WebSocket] Invalidating cache for deleted event');
        invalidateCalendarQueries();
        onEventDeleted?.(event.data);
      }
    };

    // Subscribe to events
    socket.on('calendar.event.created', handleEventCreated);
    socket.on('calendar.event.updated', handleEventUpdated);
    socket.on('calendar.event.deleted', handleEventDeleted);

    // Cleanup on unmount
    return () => {
      console.log('📅 [useCalendarSocket] Cleaning up event listeners');
      socket.off('calendar.event.created', handleEventCreated);
      socket.off('calendar.event.updated', handleEventUpdated);
      socket.off('calendar.event.deleted', handleEventDeleted);
    };
  }, [
    socket,
    connected,
    shouldDisplayEvent,
    invalidateCalendarQueries,
    calendarIds,
    onEventCreated,
    onEventUpdated,
    onEventDeleted,
  ]);

  return {
    /** WebSocket instance */
    socket,
    /** Connection status */
    connected,
  };
}
